'use client';

import { FC, useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateProductSchema } from '@/schemas';
import { Form } from '@/components/ui/form';
import { FormError } from '@/components/form';
import { createProduct } from '@/actions';
import { useProductContext } from '@/contexts/product-context';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { LoadingButton } from '@/components/loading-button';
import { toast } from 'sonner';
import { ProductFormFields } from '@/features/products/_shared/product-form-fields';

export const CreateProductForm: FC = () => {
  const [error, setError] = useState<string | undefined>('');

  const common = useTranslations('Common');
  const { status, wrap } = useSubmitStatus();
  const { dispatch } = useProductContext();

  const form = useForm<z.infer<typeof CreateProductSchema>>({
    resolver: zodResolver(CreateProductSchema),
    defaultValues: {
      name: '',
      price: 0,
      sku: '',
      description: '',
      unit: '',
    },
  });

  const onSubmit = (values: z.infer<typeof CreateProductSchema>) => {
    setError('');
    wrap(
      () =>
        new Promise<void>((resolve, reject) => {
          createProduct(values)
            .then((data) => {
              if (data.success) {
                toast.success(data.success);
                resolve();

                setTimeout(() => {
                  dispatch({ type: 'CLOSE_CREATE_PRODUCT_POPUP' });
                  form.reset();
                }, 1500);
              } else {
                const errorMessage = data.error || 'Something went wrong!';
                toast.error(errorMessage);
                setError(errorMessage);
                reject();
              }
            })
            .catch(() => {
              const errorMessage = 'Something went wrong!';
              toast.error(errorMessage);
              setError(errorMessage);
              reject();
            });
        })
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <ProductFormFields
            control={form.control}
            disabled={status === 'loading'}
          />
        </div>

        <FormError message={error} />

        <LoadingButton
          isFull={true}
          type="submit"
          status={status}
          idleText={common('confirm')}
        />
      </form>
    </Form>
  );
};
